// Briefing (spec 7.4, prototype). The AI briefing comes with M6; the lists below come from the deals now.
// Each tile leads to where its number lives, and each row says why it is listed and what to do (feedback 01.11.2026).
import type { Deal } from "../api";
import { openDeals, openInquiries, S } from "../state";
import { $, daysSince, emptyState, esc, fmtDate, today } from "../util";
import { markenHtml, verpackungSummen, volumenSummen } from "./volumen";

export const overdue = (d: Deal) => !!d.nextAction && !d.nextAction.done && !!d.nextAction.due && d.nextAction.due < today();

/** Rows a list shows before "Alle N zeigen"; lists opened in full stay open across the polls. */
const SHORT = 8;
const expanded = new Set<string>();

/** "28.10." in the current year, "28.10.25" before; a bare date is taken at noon so no time zone shifts the day. */
function day(iso: string): string {
  const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso}T12:00:00` : iso);
  if (isNaN(+d)) return esc(iso);
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", ...(d.getFullYear() === new Date().getFullYear() ? {} : { year: "2-digit" }) });
}
const tage = (n: number, dative = false) => (n === 1 ? "1 Tag" : `${n} ${dative ? "Tagen" : "Tage"}`);

/** The subject without reply prefixes and the deal's own reference, cut at a word. */
function shortSubject(s: string, ref: string): string {
  const t = s.replaceAll(`[${ref}]`, "").replace(/^\s*((aw|re|antw|wg|fw|fwd)\s*:\s*)+/i, "").replace(/\s+/g, " ").trim();
  return t.length <= 60 ? t : `${t.slice(0, 61).replace(/\s+\S*$/, "")} …`;
}

const dealLink = (d: Deal) => `<a class="link" href="#deal-${esc(d.id)}">${esc(d.company)} · ${esc(d.name)}</a>${markenHtml({ pharma: d.profil?.pharma ?? false, jahresvolumen: d.jahresvolumen })}`;
const nextStep = (d: Deal) => {
  const na = d.nextAction;
  return na && !na.done
    ? `<span class="step">Nächster Schritt: ${esc(na.text)}${na.due ? ` · <span class="due${overdue(d) ? " over" : ""}">fällig ${day(na.due)}</span>` : ""}</span>`
    : "";
};

function overdueRow(d: Deal): string {
  const due = d.nextAction!.due!;
  const late = Math.round((Date.parse(today()) - Date.parse(due)) / 864e5);
  return `${dealLink(d)}<span class="what">${esc(d.nextAction!.text)}</span><span class="why due over">fällig seit ${day(due)}, ${tage(late)}</span>`;
}

/** waiting_on 'us' follows the newest mail; without a customer mail it was set when the deal was created or by the AI. */
function waitingRow(d: Deal): string {
  const m = d.lastIn;
  const subject = m?.subject ? shortSubject(m.subject, d.ref) : "";
  const why = m
    ? `${m.fromName ? esc(m.fromName) : "Kunde"} schrieb am ${day(m.at)}${subject ? `: „${esc(subject)}“` : ""} – <b>Antwort steht aus</b>`
    : `Keine Kundenmail am Deal${d.lastActivityAt ? `, zuletzt aktiv am ${day(d.lastActivityAt)}` : ""} – <b>wir sind am Zug</b>`;
  return `${dealLink(d)}<span class="why">${why}</span>${nextStep(d)}`;
}

function staleRow(d: Deal): string {
  const why = d.lastActivityAt
    ? `Seit ${tage(Math.floor(daysSince(d.lastActivityAt)), true)} kein Kontakt (zuletzt am ${day(d.lastActivityAt)}) – <b>nachfassen</b>`
    : `Noch kein Kontakt erfasst – <b>nachfassen</b>`;
  return `${dealLink(d)}<span class="why">${why}</span>${nextStep(d)}`;
}

/** The open deals' annual volume by form instead of a revenue only a few deals carry (operator 03.11.2026). */
export function volumenKpi(od: readonly Deal[]): string {
  const sums = volumenSummen(od);
  const n = od.filter((d) => d.jahresvolumen?.posten.length).length;
  return `<a class="kpi kpi-vol" href="#volumen" title="Zur Volumen-Übersicht"><b>${sums.length ? esc(sums[0]!.text) : "–"}</b>${sums.length > 1 ? `<small>${sums
    .slice(1, 4)
    .map((x) => esc(x.text))
    .join(" · ")}</small>` : ""}<span>Jahresvolumen${n ? ` aus ${n} ${n === 1 ? "Deal" : "Deals"}` : ", noch keine Schätzung"}</span></a>`;
}

/** Packaging units per year by kind (blisters, cans, bottles, folding boxes), a service of their own (operator 03.11.2026). */
export function verpackungKpi(od: readonly Deal[]): string {
  const sums = verpackungSummen(od);
  const n = od.filter((d) => d.jahresvolumen?.verpackung?.length).length;
  return `<a class="kpi kpi-vol" href="#volumen" title="Zur Volumen-Übersicht"><b>${sums.length ? esc(sums[0]!.text) : "–"}</b>${sums.length > 1 ? `<small>${sums
    .slice(1, 4)
    .map((x) => esc(x.text))
    .join(" · ")}</small>` : ""}<span>Verpackungen/Jahr${n ? ` aus ${n} ${n === 1 ? "Deal" : "Deals"}` : ", noch keine Schätzung"}</span></a>`;
}

/** `open`: the lists shown in full. */
export function briefingHtml(open: ReadonlySet<string> = expanded): string {
  if (!S.loaded) return emptyState("Lade Briefing …", "Die Daten kommen aus der Pipeline-Datenbank.");
  const b = S.briefing;
  const od = openDeals();
  const overdueDeals = od.filter(overdue).sort((a, c) => (a.nextAction!.due ?? "").localeCompare(c.nextAction!.due ?? ""));
  // Longest waiting first: by the customer's mail, else by the last activity.
  const waitingUs = od.filter((d) => d.waitingOn === "us").sort((a, c) => (a.lastIn?.at ?? a.lastActivityAt ?? "").localeCompare(c.lastIn?.at ?? c.lastActivityAt ?? ""));
  const stale = od.filter((d) => d.waitingOn !== "us" && daysSince(d.lastActivityAt) > 14).sort((a, c) => (a.lastActivityAt ?? "").localeCompare(c.lastActivityAt ?? ""));

  const head = b
    ? `<div class="brief-head"><span class="eyebrow">Tagesbriefing · <span class="mono">${fmtDate(b.date)}</span></span><h1>${esc(b.headline || "Briefing")}</h1>${b.summary ? `<p>${esc(b.summary)}</p>` : ""}</div>`
    : `<div class="brief-head"><span class="eyebrow">Tagesbriefing</span><h1>Noch kein Briefing</h1><p>Das Tagesbriefing entsteht werktags morgens aus den Postfächern. Die Listen rechts sind schon aktuell.</p></div>`;
  const kpis = `<div class="kpis">
    <a class="kpi" href="#board" title="Zur Pipeline"><b>${od.length}</b><span>offene Deals</span></a>
    ${volumenKpi(od)}
    ${verpackungKpi(od)}
    <a class="kpi" href="#anfragen" title="Zu den Anfragen"><b>${openInquiries()}</b><span>offene Anfragen</span></a>
    <button type="button" class="kpi${overdueDeals.length ? " crit" : ""}" data-brief-goto="overdue" aria-controls="brief-overdue" title="Zur Liste der überfälligen Schritte"><b>${overdueDeals.length}</b><span>überfällige Schritte</span></button>
  </div>`;
  const items = b?.items ?? [];
  const todo = items.length
    ? `<div class="todo">${items
        .map((it) => {
          const d = it.dealId ? S.deals.get(it.dealId) : undefined;
          return `<div class="item"><span class="pill p-${esc(it.prio || "info")}">${esc(it.prio || "info")}</span>
        <div><h3>${esc(it.title)}</h3>${it.text ? `<p>${esc(it.text)}</p>` : ""}${d ? `<p class="muted" style="font-size:12px">${esc(d.company)} · ${esc(d.name)}${it.due ? ` · fällig <span class="mono">${fmtDate(it.due)}</span>` : ""}</p>` : ""}</div>
        ${d ? `<a class="btn go" href="#deal-${esc(d.id)}">Deal öffnen</a>` : it.view ? `<a class="btn go" href="#${esc(it.view)}">Öffnen</a>` : ""}</div>`;
        })
        .join("")}</div>`
    : b
      ? emptyState("Nichts Dringendes", "Im letzten Lauf ist nichts aufgefallen, das heute erledigt werden muss.")
      : emptyState("Briefing folgt", "Bis dahin zeigen die Listen rechts, was ansteht.");
  const panel = (key: string, title: string, hint: string, arr: Deal[], row: (d: Deal) => string) => {
    const all = open.has(key) || arr.length <= SHORT;
    return `<div class="panel" id="brief-${key}" tabindex="-1"><h2>${title} <small>${arr.length}</small></h2><p class="hint">${hint}</p>${
      arr.length
        ? `<ul class="rows brows">${(all ? arr : arr.slice(0, SHORT)).map((d) => `<li>${row(d)}</li>`).join("")}</ul>${
            arr.length > SHORT ? `<button type="button" class="link more" data-brief-all="${key}" aria-expanded="${all}">${all ? "Weniger zeigen" : `Alle ${arr.length} zeigen`}</button>` : ""
          }`
        : `<p class="muted" style="margin:0">Keine.</p>`
    }</div>`;
  };
  return `<div class="brief"><div>${head}${kpis}<span class="eyebrow">Heute zu tun</span><div style="height:8px"></div>${todo}</div>
    <div class="side">
      ${panel("waiting", "Wir sind dran", "Der Kunde hat zuletzt geschrieben; die Antwort von uns fehlt noch.", waitingUs, waitingRow)}
      ${panel("overdue", "Überfällige Schritte", "Nächste Schritte, deren Frist abgelaufen ist und die noch nicht erledigt sind.", overdueDeals, overdueRow)}
      ${panel("stale", "Seit 14 Tagen ruhig", "Offene Deals ohne Mail oder Aktivität seit über 14 Tagen, bei denen wir nicht am Zug sind.", stale, staleRow)}
    </div></div>`;
}

export function renderBriefing(): void {
  const el = $("#v-briefing");
  el.innerHTML = briefingHtml();
  el.onclick = (e) => {
    const t = (e.target as HTMLElement).closest<HTMLElement>("[data-brief-all], [data-brief-goto]");
    if (!t) return;
    const key = t.dataset.briefAll ?? t.dataset.briefGoto!;
    if (t.dataset.briefGoto) expanded.add(key);
    else if (!expanded.delete(key)) expanded.add(key);
    renderBriefing();
    if (!t.dataset.briefGoto) return void el.querySelector<HTMLElement>(`[data-brief-all="${key}"]`)?.focus();
    // Below the sticky header, which on a phone has three rows.
    const p = $(`#brief-${key}`, el);
    scrollTo({ top: p.getBoundingClientRect().top + scrollY - $(".top").offsetHeight - 12, behavior: "smooth" });
    p.focus({ preventScroll: true });
    p.classList.add("gs-flash");
    setTimeout(() => p.classList.remove("gs-flash"), 1600);
  };
}
